import { canViewEmailCampaigns, useCurrentUserRole } from '@components/Common/Utils/roles';
import { formatDate } from '@helpers/date';
import {
  type EmailSuppressionItem,
  type EmailSuppressionPage,
  suppressionReasonLabel,
  suppressionsUrl,
} from '@helpers/emailDelivery';
import { Tooltip } from '@mui/material';
import DeliveryChip from './DeliveryChip';
import axios from 'axios';
import { useEffect, useState } from 'react';
import './EmailBlockedChip.scss';

/**
 * The suppression entry for exactly this address, or null.
 *
 * The backend's `search` is a substring match, so `a@x.com` would also find `aa@x.com`. Only an
 * exact, case-insensitive match counts as "this address is blocked".
 */
export const exactSuppression = (email: string, page: EmailSuppressionPage | null | undefined): EmailSuppressionItem | null => {
  const want = email.trim().toLowerCase();
  if (!want) return null;
  return (page?.content ?? []).find((s) => (s.email ?? '').trim().toLowerCase() === want) ?? null;
};

/**
 * "Email blocked", beside an account's email, when the platform no longer mails it.
 *
 * Super Admin and HR Lead only, and the role check comes before the request, not after: the
 * suppression list is refused to anyone else on the BFF, so an HR or Marketing user would only collect a 403, and the
 * addresses and reasons on it are not theirs to see. While the role is still loading nothing is
 * asked either.
 */
export const EmailBlockedChip: React.FC<{ email?: string | null }> = ({ email }) => {
  const role = useCurrentUserRole();
  // An HR Lead reads the suppression list in Email campaigns, so the chip is hers too.
  const allowed = canViewEmailCampaigns(role);
  const address = (email ?? '').trim();
  const [entry, setEntry] = useState<EmailSuppressionItem | null>(null);

  useEffect(() => {
    setEntry(null);
    if (!allowed || !address) return undefined;
    let live = true;
    axios
      .get<EmailSuppressionPage>(suppressionsUrl(address, 0, 1))
      .then((r) => { if (live) setEntry(exactSuppression(address, r.data)); })
      // A failed lookup shows nothing. The chip is a hint on someone else's page, not that page's error.
      .catch(() => undefined);
    return () => { live = false; };
  }, [allowed, address]);

  if (!entry) return null;

  const since = entry.firstAt ? ` since ${formatDate(entry.firstAt)}` : '';
  const why = entry.lastDiagnostic ? `. ${entry.lastDiagnostic}` : '';
  return (
    <Tooltip title={`${suppressionReasonLabel(entry.reason)}${since}${why}`}>
      <span><DeliveryChip tone="error" label="Email blocked" /></span>
    </Tooltip>
  );
};

/**
 * An account's email as a workspace field shows it, with the chip beside it when it is blocked.
 * Returns '' for no address so WorkspaceField still draws its dash.
 */
export const emailWithBlockState = (email?: string | null): React.ReactNode => {
  const address = (email ?? '').trim();
  if (!address) return '';
  return (
    <span className="email-blocked-field">
      <span className="email-blocked-field__address">{address}</span>
      <EmailBlockedChip email={address} />
    </span>
  );
};

export default EmailBlockedChip;
